//------------------------------------------------------------------------
// Vertical Bar Chart
//------------------------------------------------------------------------
// NOTE: Only import the necessary modules
// https://www.chartjs.org/docs/latest/getting-started/integration.html
// NOTE: Treeshaking doesn’t seem to be working when importing everything
// https://github.com/chartjs/Chart.js/issues/10163
// e.g. import Chart from 'chart.js/auto';
import {Chart, BarElement, BarController, Tooltip, LinearScale, CategoryScale} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import debounce from "lodash/debounce";
import { axis_font_opts, cursorPositioner } from '../helpers/chart-js-helpers';
Chart.register(BarElement, BarController, Tooltip, LinearScale, CategoryScale);

/**
 * Custom positioner - Allows for the tooltip to follow the cursor's pointer
 *
 * Notes:
 * - https://www.chartjs.org/docs/latest/configuration/tooltip.html#custom-position-modes
 * - https://codepen.io/AZIIZoom/pen/eYzJNZW?editors=1010
 * @function Tooltip.positioners.myCustomPositioner
 * @param elements {Chart.Element[]} the tooltip elements
 * @param eventPosition {Point} the position of the event in canvas coordinates
 * @returns {TooltipPosition} the tooltip position
 */
Tooltip.positioners.cursor = cursorPositioner;

class VerticalBarChart {
  constructor(config) {
    this.el = config.el;
    this.canvas = this.el.querySelector('canvas');

    if (!this.canvas) {
      console.error(`Unable to initialize vertical bar chart, no matching element for ${this.el}.`);
      return false;
    }

    this.setData();
    this.setChartConfig();
    this.init();
  }

  init() {
    this.chart = new Chart(this.canvas, {
      type: 'bar',
      data: this.data,
      plugins: this.plugins,
      options: this.chartOpts
    });

    this.el.addEventListener('mouseover-chart', debounce((evt) => {
      let { dataPoints } =  evt.detail.tooltip;
      let { chart } = evt.detail;

      if (dataPoints.length < 1) {
        return false;
      }

      let selectedDatasetIndex = dataPoints[0].datasetIndex;
      let selectedIndex = dataPoints[0].dataIndex;

      chart.data.datasets[selectedDatasetIndex].backgroundColor.forEach((color, index, colors) => {
        let colorLength = color.length;

        // Remove the original opacity from the color
        let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

        if (selectedIndex === index) {
          colors[index] = baseColor;
        } else {
          colors[index] = baseColor + '99';
        }
      });

      chart.update('none');
    }), 10);

    this.el.addEventListener('mouseout-chart', debounce((evt) => {
      let { chart } = evt.detail;

      chart.data.datasets[0].backgroundColor.forEach((color, index, colors) => {
        let colorLength = color.length;
        // Remove the original opacity from the color
        let baseColor = color.length === 9 ? color.slice(0, colorLength - 2) : color;

        colors[index] = baseColor + 'e0';
      });

      chart.update('none');
    }, 10));
  }

  externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = chart.canvas.nextElementSibling;

    // Custom event detail that is being passed through
    let detail = {
      detail: {
        el: this.el,
        tooltip,
        chart
      }
    };

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      const mouseoutChart = new CustomEvent("mouseout-chart", detail);

      this.el.dispatchEvent(mouseoutChart);
      return;
    }

    // console.log(tooltip);

    // Set Text
    if (tooltip.dataPoints) {
      let dataPoints = tooltip.dataPoints || [];
      const tooltipFragment = document.createDocumentFragment();

      dataPoints.forEach(item => {
        let titleEl = document.createElement('span');
        let titleText = document.createTextNode(item.label);
        let valueEl = document.createElement('span');
        let valueText = document.createTextNode(item.formattedValue);

        titleEl.classList.add('ChartTooltip-title');
        valueEl.classList.add('ChartTooltip-value');

        titleEl.appendChild(titleText);
        valueEl.appendChild(valueText);

        tooltipFragment.appendChild(titleEl);
        tooltipFragment.appendChild(valueEl);
      });

      while(tooltipEl.firstChild) {
        tooltipEl.firstChild.remove();
      }

      tooltipEl.appendChild(tooltipFragment);
    }

    const { offsetLeft, offsetTop } = chart.canvas;
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = offsetLeft + (tooltip.caretX + 10) + 'px';
    tooltipEl.style.top = offsetTop + (tooltip.caretY + 10) + 'px';

    // Emit event when we are hovering over the segment
    const mouseoverChart = new CustomEvent("mouseover-chart", detail);
    this.el.dispatchEvent(mouseoverChart);
  }

  /**
   * Retrieves data-* attributes from the Chart's container and create instance variables
   * of each in the correct format for Chart.js
   *
   * These are the data-* attributes that are being used:
   * - data-segments = the segments from the block's segments repeater field
   * - data-title = the chart's title
   * - data-type = the type of circle chart that is being used
   *
   * isntance variables that are being created:
   * - this.dataValues
   * - this.colors
   * - this.title
   * - this.type
   * @return null
   */
  setData() {
    let data = JSON.parse(this.el.getAttribute('data-bars'));

    this.dataColors = data.map(item => item.color + 'e0');
    this.dataLabels = data.map(item => item.label);
    this.dataValues = data.map(item => parseInt(item.value, 10));

    this.data = {
      labels: this.dataLabels,
      datasets: [{
        label: this.el.getAttribute('data-title'),
        data: this.dataValues,
        backgroundColor: this.dataColors,
        borderColor: this.dataColors
      }]
    };

    // let type = this.el.getAttribute('data-type');
    // let title = this.el.getAttribute('data-title');

    // this.dataLabels = data.map(segment => {
    //   return segment.name;
    // });

    // this.dataValues = data.map(segment => segment.value);

    // this.colors = data.map(segment => segment.color);

    // this.title = title;
    // this.type = type;
  }

  setChartConfig() {
    const userDefinedOpts = JSON.parse(this.el.getAttribute('data-chart-opts'));

    // plugin that displays the featured in the center of the donut chart
    let mql = window.matchMedia("(min-width: 700px)");

    mql.addEventListener('change', () => {
      // Returning the same values that onResize would for chart.js
      let width  = window.innerWidth;
      let tooltip = (width > 700) ? this.externalTooltipHandler.bind(this) : false;

      this.chart.options.plugins.tooltip.external = tooltip;
      this.chart.update();
    });

    // const maxRotation = (() => {
    //   return this.dataValues.length > 2 ? -45 : 50;
    // })();

    // const minRotation = (() => {
    //   return this.dataValues.length > 2 ? -45 : 0;
    // })();

    // const padding = (() => {
    //   return this.dataValues.length < 2 ? 0 : 30
    // })();

    let datalabelsFontSize = (window.innerWidth > 700) ? 22 : 18;
    let barLabelFontSize = (window.innerWidth > 700) ? 18 : 16;
    let tickMarkFontSize = (window.innerWidth > 700) ? 16 : 14;
    let chartFontColor = "#404040"; // $c-default / fs-color("text", "gray-dark");

    // Set opts for chart.js
    this.chartOpts = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: userDefinedOpts.y_axis_title ? true : false, // displays y axis title via user specified value
            text: userDefinedOpts.y_axis_title, // displays y axis title via user specified value
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: userDefinedOpts.show_y_axis, // displays y axis ticks, e.g. the step counts
            font: {
              size: tickMarkFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            stepSize: () => {
              let max = this.dataValues.reduce((accumulator, currentValue) => {
                return Math.max(accumulator, currentValue)
              });

              // We want a maximum of six steps per graph
              let step = Math.ceil(max / 6);

              // We are rounding it up to the nearest tenth to prevent odd numbers
              // https://stackoverflow.com/a/11022517
              return Math.ceil(step / 10) * 10;
            },
          },
          grid: {
            display: userDefinedOpts.show_gridlines // shows or hides the horizontal gridlines on the chart
          },
          suggestedMax: () => {
            // The maximum number for the Y axis value. This is done dynamically
            // so we can have space for the data labels to appear of the bar without worrying about collision
            let max = this.dataValues.reduce((accumulator, currentValue) => {
              return Math.max(accumulator, currentValue)
            });

            return max * 1.15; // return 110% of the maximum value
          }
        },
        x: {
          title: {
            display: userDefinedOpts.x_axis_title ? true : false,
            text: userDefinedOpts.x_axis_title,
            font: axis_font_opts(),
            color: chartFontColor
          },
          ticks: {
            display: true, // setting false hides the values on the x axis
            font: {
              size: barLabelFontSize,
              family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
              weight: 'normal'
            },
            color: chartFontColor
            // minRotation: minRotation,
            // maxRotation: maxRotation,
            // padding: 20,
            // autoSkipPadding: 150
          },
          grid: {
            display: false
          },
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
          position: 'cursor',
          // We are binding the method to have access to this.toolTipEl
          external: mql.matches ? this.externalTooltipHandler.bind(this) : false,
        }
      },
      onResize: debounce((chart, size) => {
        let { width } = size;

        let mobileTicksFontX = {
          size: 16,
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          weight: 'normal'
        };

        let desktopTicksFontX = {
          size: 18,
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          weight: 'normal'
        };

        let datalabelsFont = {
          size: datalabelsFontSize ,
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          weight: 'bold'
        };

        let tickOpts = {
          // display ticks only if content author selected to display them and screen size is over 700
          display: (userDefinedOpts.show_y_axis && width > 700) ? userDefinedOpts.show_y_axis : false,
          font: {
            size: tickMarkFontSize,
            family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
            weight: 'normal'
          },
          color: chartFontColor
        };

        // adding an extra conditional to prevent updating the chart every time when the screen size changes
        chart.options.scales.y.ticks = tickOpts;
        chart.options.scales.x.ticks.font = (width > 700) ? desktopTicksFontX : mobileTicksFontX;

        if (userDefinedOpts.show_data_series_counts) {
          chart.options.plugins.datalabels.font = datalabelsFont;
        }

        chart.update();
      }, 10),
    };

    // Add Chart.js plugins here
    this.plugins = [];

    // Add data labels if user wants it to appear by checking the show_data_series_count parameter
    if (userDefinedOpts.show_data_series_counts) {
      let datalabelsFontSize = (window.innerWidth > 700) ? 22 : 18;

      this.plugins.push(ChartDataLabels);
      this.chartOpts.plugins.datalabels = {
        font: {
          size: datalabelsFontSize,
          family: "'Luma Sans', 'Helvetica', 'Arial', sans-serif",
          weight: 'bold',
          lineHeight: 1.3,
        },
        color: '#480751', // fs-color("text", "purple")
        anchor: 'end',
        clamp: true, // only put the data label inside of the chart
        align: 'top'
      }
    }
  }
}

const init = () => {
  const verticalBarCharts = document.querySelectorAll('[data-vertical-bar-chart]');

  if (verticalBarCharts) {
    verticalBarCharts.forEach(chart => {
      new VerticalBarChart({el: chart});
    })
  }
}

// Init on initial page load
init();
